// Hasta detayı › İletişim › Mesajlar (2026-10-07) — tek hastanın konuşması, Mesajlar
// sayfasına gitmeden. Akış bileşeni Mesajlar sayfasıyla ortak (MessageThread). Konu yoksa
// ilk mesajla oluşur (sunucu); açmak kayıt oluşturmaz.

import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import useAuth from "../../../hooks/useAuth";
import usePageVisible from "../../../hooks/usePageVisible";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useAlert } from "../../../context/AlertProvider";
import { useClinicTheme } from "../../../theme";
import {
  useMarkMessagesReadMutation,
  useMessageThreadQuery,
  useOlderMessagesMutation,
  useSendMessageMutation,
  useSendMessageTemplateMutation,
} from "../../../store/messageApi";
import MessageThread from "../../../pages/Messages/MessageThread";

const POLL_MS = 20 * 1000;
const THREAD_LIMIT = 40;

export default function PatientMessagesPanel({ patientId }) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showError, showSuccess } = useAlert();
  const visible = usePageVisible();
  const canSend = useHasPermission("messages:send");

  const {
    currentData: threadRes,
    isLoading,
    isFetching,
    isError,
  } = useMessageThreadQuery(
    { token, patientId, limit: THREAD_LIMIT },
    { skip: !token || !patientId, pollingInterval: visible ? POLL_MS : 0 }
  );
  const thread = threadRes?.data || null;
  const convId = thread?.conversation?._id || null;

  // ── Eski sayfalar ──
  const [older, setOlder] = useState([]);
  const [olderHasMore, setOlderHasMore] = useState(null);
  const [fetchOlder, { isLoading: loadingOlder }] = useOlderMessagesMutation();
  useEffect(() => {
    setOlder([]);
    setOlderHasMore(null);
  }, [patientId]);
  const hasMoreOlder = olderHasMore === null ? Boolean(thread?.hasMore) : olderHasMore;
  const loadOlder = async () => {
    const first = older[0] || thread?.messages?.[0];
    if (!first || !convId) return;
    try {
      const res = await fetchOlder({ token, conversationId: convId, before: first.createdAt, limit: THREAD_LIMIT }).unwrap();
      setOlder((prev) => [...(res?.data?.messages || []), ...prev]);
      setOlderHasMore(Boolean(res?.data?.hasMore));
    } catch (err) {
      showError(err?.data?.message || "Eski mesajlar yüklenemedi");
    }
  };

  // ── Okundu ──
  const [markRead] = useMarkMessagesReadMutation();
  const unread = thread?.conversation?.unread || 0;
  const lastAt = thread?.conversation?.lastMessageAt;
  useEffect(() => {
    if (!convId || unread <= 0 || !visible) return;
    markRead({ token, conversationId: convId }).catch(() => {});
  }, [convId, unread, lastAt, visible, token, markRead]);

  // ── Gönder ──
  const [sendMessage, { isLoading: sending }] = useSendMessageMutation();
  const handleSend = async (text, channel = "app") => {
    try {
      const body = convId ? { conversationId: convId } : { patientId };
      await sendMessage({ token, ...body, text, channel }).unwrap();
      return true;
    } catch (err) {
      showError(err?.data?.message || "Mesaj gönderilemedi");
      return false;
    }
  };

  // ── WhatsApp manuel şablonu (24 saat penceresi kapalıyken listeden seçilir) ──
  const [sendTemplate, { isLoading: sendingTemplate }] = useSendMessageTemplateMutation();
  const handleSendTemplate = async (templateKey) => {
    try {
      const body = convId ? { conversationId: convId } : { patientId };
      await sendTemplate({ token, ...body, templateKey }).unwrap();
      showSuccess("WhatsApp şablonu gönderildi");
    } catch (err) {
      showError(err?.data?.message || "Şablon gönderilemedi");
    }
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <MessageThread
        thread={thread}
        loading={isLoading || (isFetching && !thread)}
        error={isError}
        olderMessages={older}
        hasMoreOlder={hasMoreOlder}
        loadingOlder={loadingOlder}
        onLoadOlder={loadOlder}
        onSend={handleSend}
        sending={sending}
        onSendTemplate={handleSendTemplate}
        sendingTemplate={sendingTemplate}
        canSendPermission={canSend}
        hideHeader
        palette={palette}
      />
    </Box>
  );
}
